import confetti from "canvas-confetti";
import { Button } from "primereact/button";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { i18n, initializeI18n } from "../localization/i18n";
import { translate } from "../localization/translateService";
import { getQuizItem, postFreeQuizAnswer, associateGuestEmail } from "../services/quizService";
import resultTestTitleImage from "../stories/assets/pre-test-result-img.png";
import Stepper from "./Stepper";
import { TestQuestionGroup } from "./TestQuestionGroup";
import KeduLoading from "./loading/KeduLoading";
import LoadingButton from "./common/LoadingButton";
import GuestEmailVerificationModal from "./GuestEmailVerificationModal";
import { getCurrentUserFromLocalStorage } from "../helpers/localStorageHelper";

export const TestFreeTest = forwardRef(({ handleForceCloseModal, handleCloseModal, setIsNeedConfirmModal }, ref) => {
  const [quizDataState, setQuizDataState] = useState(null);
  const [questionView, setQuestionView] = useState(true);
  const [resultView, setResultView] = useState(false);
  const [emptyView, setEmptyView] = useState(false);
  const [testAnswerState, setTestAnswerState] = useState([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [stepperLength, setStepperLength] = useState(0);
  const [preTestResultId, setPreTestResultId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const startTimeRef = useRef(null);
  const languageCurrentValueRef = useRef(null);

  const quizId = useRef(null);

  const [freeTestTranslations, setFreeTestTranslations] = useState({
    startPreTestTitle: "",
    startButtonLabel: "",
    notStartButtonLabel: "",
    nextButtonLabel: "",
    submitButtonLabel: "",
    backButtonLabel: "",
    yourResultTitle: "",
    finishButtonLabel: "",
    closeButtonLabel: "",
    finishSubTitle1: "",
    finishSubTitle2: "",
    finishSubTitle3: "",
    preTestTitle: "",
    congratsTitle: "",
    typeTitle: "",
    scoreTitle: "",
    correctTitle: "",
    wrongTitle: "",
    correctRateTitle: "",
    timeTakenTitle: "",
    emptyPreTestTitle: "",
  });

  const canvasRef = useRef(null);

  const myConfetti = confetti.create(canvasRef.current, {
    resize: true,
    useWorker: true,
  });

  const handleClickFirework = () => {
    myConfetti({
      particleCount: 50,
      spread: 90,
      startVelocity: 25,
      gravity: 0.5,
      angle: 110,
      ticks: 200,
      origin: { x: 0.7, y: 0.5 },
    });
    myConfetti({
      particleCount: 50,
      spread: 90,
      startVelocity: 25,
      gravity: 0.5,
      angle: 70,
      ticks: 200,
      origin: { x: 0.3, y: 0.5 },
    });
  };

  const handleStartQuiz = () => {
    setResultView(false);
    setQuestionView(true);
    setIsNeedConfirmModal(true);
    setTestAnswerState([]);
    setPreTestResultId(null);
    startTimeRef.current = Date.now();
  };

  const fetchQuizData = async () => {
    const data = await getQuizItem();
    const questions = data?.data?.[0]?.questions;

    if (!Array.isArray(questions) || questions.length === 0) {
      setQuestionView(false);
      setEmptyView(true);
      return;
    }
    quizId.current = data?.quizId;
    setQuizDataState(data?.data);
    setStepperLength(data?.data?.[0]?.questions?.length ?? 0);
    handleStartQuiz();
  };

  const handleResetAnswer = () => {
    setQuestionIndex(0);
    setTestAnswerState([]);
  };

  const navigateToResult = () => {
    sessionStorage.setItem("pretest_access", preTestResultId);
    window.location.href = `/${languageCurrentValueRef.current}/pretest/result/${preTestResultId}`;
    handleForceCloseModal();
    handleResetAnswer();
  };

  const handleFinishQuiz = () => {
    const currentUser = getCurrentUserFromLocalStorage();
    if (!currentUser) {
      setShowEmailModal(true);
    } else {
      navigateToResult();
    }
  };

  const handleCloseQuiz = () => {
    handleCloseModal();
  };

  const handleNextBtn = () => {
    setQuestionIndex((prev) => prev + 1);
  };

  const handleBackBtn = () => {
    setQuestionIndex((prev) => prev - 1);
  };

  const handleChangeAnswer = (newAnswer) => {
    const copiedTestAnswer = JSON.parse(JSON.stringify(testAnswerState));
    const answerIndex = copiedTestAnswer.findIndex((answer) => answer.questionId === newAnswer.questionId);
    if (answerIndex !== -1) {
      copiedTestAnswer[answerIndex] = newAnswer;
    } else {
      copiedTestAnswer.push(newAnswer);
    }
    setTestAnswerState(copiedTestAnswer);
  };

  const handleClickSubmit = () => {
    const currentUser = getCurrentUserFromLocalStorage();
    handleSubmitQuiz(currentUser?.email ?? null);
  };

  const handleGuestVerified = async (verifiedEmail) => {
    setShowEmailModal(false);
    try {
      await associateGuestEmail(preTestResultId, verifiedEmail);
    } catch {
      // non-critical: navigate anyway
    }
    navigateToResult();
  };

  const handleSubmitQuiz = (email = null) => {
    setIsSubmitting(true);
    let testTime = 0;
    if (startTimeRef.current) {
      const endTime = Date.now();
      testTime = endTime - startTimeRef.current;
    }
    setIsNeedConfirmModal(false);
    setQuestionView(false);
    const answerRequestData = quizDataState?.flatMap((questionGroup) => {
      return questionGroup?.questions?.map((questionItem) => ({
        ...questionItem,
        userAnswer: testAnswerState.find((question) => question?.questionId === questionItem.id)?.answerText ?? null,
        answers: questionItem?.answers?.map((answer) => ({
          ...answer,
          userSelect: testAnswerState.find((question) => question?.questionId === questionItem.id)?.answerId?.includes(answer?.id) ? true : false,
        })),
      }));
    });
    const requestData = {
      answerData: answerRequestData,
      isAnonymous: !email,
      testTime: Math.floor(testTime / 1000),
      email: email,
    };

    postFreeQuizAnswer(quizId.current, requestData)
      .then((data) => {
        if (data) {
          handleClickFirework();
          setPreTestResultId(data?.publicId);
          setResultView(true);
        }
        setIsSubmitting(false);
      })
      .catch(() => {
        setIsSubmitting(false);
      });
  };

  useImperativeHandle(ref, () => ({
    triggerResetAnswer: () => {
      handleResetAnswer();
    },
  }));

  useEffect(() => {
    fetchQuizData();
    const loadTranslations = async () => {
      const startPreTestTitle = await translate("UMBRACO_LANDING.FREE_TEST.TITLE.START_PRE_TEST");
      const startButtonLabel = await translate("UMBRACO_LANDING.FREE_TEST.BUTTON.START");
      const notStartButtonLabel = await translate("UMBRACO_LANDING.FREE_TEST.BUTTON.NOT_START");
      const nextButtonLabel = await translate("UMBRACO_LANDING.FREE_TEST.BUTTON.NEXT");
      const submitButtonLabel = await translate("UMBRACO_LANDING.FREE_TEST.BUTTON.SUBMIT");
      const backButtonLabel = await translate("UMBRACO_LANDING.FREE_TEST.BUTTON.BACK");
      const finishButtonLabel = await translate("UMBRACO_LANDING.FREE_TEST.BUTTON.FINISH");
      const closeButtonLabel = await translate("UMBRACO_LANDING.FREE_TEST.BUTTON.CLOSE");
      const yourResultTitle = await translate("UMBRACO_LANDING.FREE_TEST.TITLE.RESULT");
      const finishSubTitle1 = await translate("UMBRACO_LANDING.FREE_TEST.TITLE.FINISH_1");
      const finishSubTitle2 = await translate("UMBRACO_LANDING.FREE_TEST.TITLE.FINISH_2");
      const finishSubTitle3 = await translate("UMBRACO_LANDING.FREE_TEST.TITLE.FINISH_3");
      const preTestTitle = await translate("UMBRACO_LANDING.FREE_TEST.TITLE.PRE_TEST");
      const typeTitle = await translate("UMBRACO_LANDING.FREE_TEST.TITLE.TYPE");
      const scoreTitle = await translate("UMBRACO_LANDING.FREE_TEST.TITLE.SCORE");
      const correctTitle = await translate("UMBRACO_LANDING.FREE_TEST.TITLE.CORRECT");
      const wrongTitle = await translate("UMBRACO_LANDING.FREE_TEST.TITLE.WRONG");
      const correctRateTitle = await translate("UMBRACO_LANDING.FREE_TEST.TITLE.CORRECT_RATE");
      const timeTakenTitle = await translate("UMBRACO_LANDING.FREE_TEST.TITLE.TIME_TAKEN");
      const emptyPreTestTitle = await translate("UMBRACO_LANDING.FREE_TEST.TITLE.EMPTY_PRE_TEST");

      setFreeTestTranslations({
        startPreTestTitle,
        startButtonLabel,
        notStartButtonLabel,
        nextButtonLabel,
        submitButtonLabel,
        backButtonLabel,
        yourResultTitle,
        finishButtonLabel,
        closeButtonLabel,
        finishSubTitle1,
        finishSubTitle2,
        finishSubTitle3,
        preTestTitle,
        typeTitle,
        scoreTitle,
        correctTitle,
        wrongTitle,
        correctRateTitle,
        timeTakenTitle,
        emptyPreTestTitle,
      });
    };

    loadTranslations();

    if (!i18n.isInitialized) {
      initializeI18n().then(() => {
        languageCurrentValueRef.current = i18n.language;
      });
    } else {
      languageCurrentValueRef.current = i18n.language;
    }
  }, []);

  return (
    <div className="test-free-test-wrapper">
      <GuestEmailVerificationModal
        isOpen={showEmailModal}
        onVerified={handleGuestVerified}
        onClose={() => setShowEmailModal(false)}
      />
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          zIndex: 10,
        }}
      />
      {questionView && stepperLength === 0 && (
        <div className="mx-auto start-view min-h-10rem">
          <KeduLoading />
        </div>
      )}

      {questionView && stepperLength !== 0 && (
        <div className="pretest-card">
          <Stepper stepperLength={stepperLength} stepperCurrentIndex={questionIndex} />
          <div className="question-view-content">
            {quizDataState && (
              <TestQuestionGroup testAnswers={testAnswerState} questionGroupData={quizDataState?.[0]} questionIndex={questionIndex} handleChangeAnswer={handleChangeAnswer} />
            )}
          </div>
          {quizDataState && (
            <div className="pretest-nav">
              <button type="button" className="pretest-endtest" onClick={handleCloseQuiz}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M9.57 5.93 3.5 12l6.07 6.07" />
                  <path d="M20.5 12H3.67" />
                </svg>
                <span className="keduall-typography text-14-normal-400">{freeTestTranslations.closeButtonLabel}</span>
              </button>
              <div className="pretest-nav__right">
                {questionIndex !== 0 && (
                  <Button className="keduall-buttons button-outlined-default-normal-secondary mr-2" onClick={handleBackBtn}>
                    <p className="keduall-typography text-16-semibold-600">{freeTestTranslations.backButtonLabel}</p>
                  </Button>
                )}
                {questionIndex === quizDataState?.[0]?.questions?.length - 1 ? (
                  <LoadingButton className="keduall-buttons button-filled-default-normal-primary" loading={isSubmitting} onClick={handleClickSubmit}>
                    <p className="keduall-typography text-16-semibold-600">{freeTestTranslations.submitButtonLabel}</p>
                  </LoadingButton>
                ) : (
                  <Button className="keduall-buttons button-filled-default-normal-primary" onClick={handleNextBtn}>
                    <p className="keduall-typography text-16-semibold-600">{freeTestTranslations.nextButtonLabel}</p>
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {resultView && (
        <div className="flex flex-column gap-4 justify-content-between align-items-center">
          <div className="flex align-items-center">
            <p className="font-size-42 height-60 width-60">🎉</p>
            <img src={resultTestTitleImage} alt="congrats-image" className="width-96 height-96" />
            <p className="-rotate-90 font-size-42 height-60 width-60">🎉</p>
          </div>
          <div>
            <h3 className="keduall-typography heading-h4-normal-400 text-third-txt-color max-width-480 text-center">{freeTestTranslations.yourResultTitle}</h3>
            <h3 className="keduall-typography heading-h4-normal-400 text-third-txt-color max-width-480 text-center">
              {freeTestTranslations.finishSubTitle1}&nbsp;
              <span className="nowrap">
                '<span className="text-primary-txt-color">{freeTestTranslations.finishSubTitle2}</span>'
              </span>
              &nbsp;
              <span> {freeTestTranslations.finishSubTitle3}</span>
            </h3>
          </div>
          <Button className="keduall-buttons button-filled-default-large-primary mb-6" onClick={handleFinishQuiz}>
            <h6 className="keduall-typography heading-h6-semibold-600">{freeTestTranslations.finishButtonLabel}</h6>
          </Button>
        </div>
      )}

      {emptyView && (
        <div className="flex flex-column gap-4 justify-content-between align-items-center">
          <div>
            <h3 className="keduall-typography heading-h4-normal-400 text-third-txt-color max-width-480 text-center">{freeTestTranslations.emptyPreTestTitle}</h3>
          </div>
          <Button className="keduall-buttons button-filled-default-normal-primary" onClick={handleCloseQuiz}>
            <p className="keduall-typography text-16-semibold-600">{freeTestTranslations.closeButtonLabel}</p>
          </Button>
        </div>
      )}
    </div>
  );
});
